import { useEffect, useMemo, useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { formatForDatetimeLocal } from '../../../lib/formatForDatetimeLocal.js';
import FileInput from '../../FileInput';
import API from '../../../API';
import { isFileAcceptable } from '../../../lib/fileUtils';
import styles from './FieldTypes.module.css';

const TextBox = ({ state, setState, field }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='text' className={styles.input} maxLength='100' value={state[id]} placeholder='Your answer' onChange={e => setState(prev => ({ ...prev, [id]: e.target.value}))} style={field.styles || {}} />
        </div>
    );
};

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const Email = ({ state, setState, field }) => {
    const { id, name, required } = field;
    const emailValue = state[id] || '';
    const isInvalid = emailValue && !isValidEmail(emailValue);
    
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input 
                id={id} 
                type='email' 
                className={`${styles.input} ${isInvalid ? styles.inputError : ''}`}
                maxLength='100' 
                value={state[id]} 
                placeholder='example@email.com' 
                onChange={e => setState(prev => ({ ...prev, [id]: e.target.value}))} 
            />
            {isInvalid && <div style={{fontSize: '.85rem', color: '#e74c3c', marginTop: '.25rem'}}>Please enter a valid email address</div>}
        </div>
    );
};

const isValidPhone = (phone) => {
    const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
    return phone.length >= 10 && phoneRegex.test(phone);
};

const normalizePhone = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // If it's empty or too short, return original
    if (digits.length < 10) return phone;
    
    // Format based on length
    if (digits.length === 10) {
        // US number without country code: (123) 456-7890
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length === 11 && digits[0] === '1') {
        // US number with country code: +1 (123) 456-7890
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
    } else if (digits.length > 11) {
        // International number: +XX (XXX) XXX-XXXX
        const countryCode = digits.slice(0, digits.length - 10);
        const areaCode = digits.slice(digits.length - 10, digits.length - 7);
        const prefix = digits.slice(digits.length - 7, digits.length - 4);
        const lineNumber = digits.slice(digits.length - 4);
        return `+${countryCode} (${areaCode}) ${prefix}-${lineNumber}`;
    }
    
    return phone;
};

const Phone = ({ state, setState, field }) => {
    const { id, name, required } = field;
    const phoneValue = state[id] || '';
    const isInvalid = phoneValue && !isValidPhone(phoneValue);
    
    const handleBlur = () => {
        if (phoneValue && isValidPhone(phoneValue)) {
            const normalized = normalizePhone(phoneValue);
            setState(prev => ({ ...prev, [id]: normalized }));
        }
    };
    
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input 
                id={id} 
                type='tel' 
                className={`${styles.input} ${isInvalid ? styles.inputError : ''}`}
                maxLength='100' 
                value={state[id]} 
                placeholder='(123) 456-7890' 
                onChange={e => setState(prev => ({ ...prev, [id]: e.target.value}))}
                onBlur={handleBlur}
            />
            {isInvalid && <div style={{fontSize: '.85rem', color: '#e74c3c', marginTop: '.25rem'}}>Please enter a valid phone number</div>}
        </div>
    );
};

const Address = ({ state, setState, field }) => {
    const { id, name, required } = field;
    const addressValue = state[id] || { street: '', city: '', state: '', postalCode: '' };
    
    const handleAddressChange = (fieldName, value) => {
        setState(prev => ({
            ...prev,
            [id]: {
                ...(prev[id] || {}),
                [fieldName]: value
            }
        }));
    };
    
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', width: '100%' }}>
                <input
                    type='text'
                    className={styles.input}
                    placeholder='Street Address'
                    value={addressValue.street || ''}
                    onChange={e => handleAddressChange('street', e.target.value)}
                />
                <input
                    type='text'
                    className={styles.input}
                    placeholder='City / Township'
                    value={addressValue.city || ''}
                    onChange={e => handleAddressChange('city', e.target.value)}
                />
                <div style={{ display: 'flex', gap: '.5rem' }}>
                    <input
                        type='text'
                        className={styles.input}
                        placeholder='State / Province'
                        value={addressValue.state || ''}
                        onChange={e => handleAddressChange('state', e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <input
                        type='text'
                        className={styles.input}
                        placeholder='Postal Code'
                        value={addressValue.postalCode || ''}
                        onChange={e => handleAddressChange('postalCode', e.target.value)}
                        style={{ flex: 1 }}
                    />
                </div>
            </div>
        </div>
    );
};

const TextArea = ({ state, setState, field }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <textarea id={id} value={state[id]} className={styles.textarea} placeholder='Your answer' maxLength={256} onChange={e => setState(prev => ({ ...prev, [id]: e.target.value}))} style={field.styles || {}}></textarea>
        </div>
    );
};

const Number = ({ state, setState, field }) => {
    const { id, name, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='number' placeholder={0} className={styles.input} value={state[id]} onFocus={(e) => e.target.addEventListener("wheel", (e) => { e.preventDefault() }, { passive: false })} onChange={e => setState(prev => ({ ...prev, [id]: e.target.value}))}></input>
        </div>
    );
};

const Timestamp = ({ state, setState, field, isTimestamp }) => {
    const { id, name, required } = field;

    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>{(required && !isTimestamp) ? <>{name}<sup><i className='far fa-asterisk' /></sup></> : name}</label>
            <input id={id} type='datetime-local' className={styles.input} value={state[id] ? formatForDatetimeLocal(new Date(state[id])) : ''} onChange={(e) => setState(prev => ({...prev, [id]: e.target.value}))} disabled={isTimestamp} />
        </div>
    );
};

const Dropdown = ({ state, setState, field }) => {
    const { id, options, name, required } = field;
    return (
        <div className={styles.inputGroupSelect}>
            <label htmlFor={id} className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <select id={id} value={state[id] ?? ''} className={styles.dropdown} onChange={e => setState(prev => ({ ...prev, [id]: e.target.value}))} >
                <option value='' disabled>Select an option</option>
                {options?.map((name, index) => (<option key={name + index} className={styles.option} value={name}>{name}</option>))}
            </select>
        </div>
    );
};

const Signature = ({ field, sigCanvas, setSigCanvas, setState, edit }) => {
    const { id, name, required } = field;

    useEffect(() => {
        if (edit && sigCanvas) {
            sigCanvas.off();
        };
    },[edit, sigCanvas]);

    return (
        <div className={styles.inputGroup}>
            <div className={styles.signatureLabelContainer}>
                <label className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
                {edit ? <p style={{paddingLeft: '.5rem', marginBottom: '0.25rem', fontSize: '.85rem'}}>Signatures cannot be edited.</p> : <button className={styles.btn} onClick={() => { sigCanvas.clear(); setState(prev => ({ ...prev, [id]: false } )) }} disabled={edit}>Clear</button>}
            </div>
            <SignatureCanvas ref={(ref) => {setSigCanvas(ref)}} onEnd={() => setState(prev => ({...prev, [id]: sigCanvas?.isEmpty() ? true : false}))} penColor='black' canvasProps={{ width: 350, height: 100, className:`${styles.trackerCanvas}` }} />
        </div>
    );
};

const SingleSelect = ({ state, setState, field }) => {
    const { id, name, options, required } = field;
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            {options.map((opt, index) => (
                <div className={styles.singleSelectGroup} key={opt + index}>
                    <label htmlFor={id+index} className={styles.singleSelect}>
                        <input 
                            type='radio' 
                            name={`${field}_options`} 
                            id={id+index} 
                            value={opt} 
                            checked={state[id] === opt} 
                            onChange={(e) => setState(prev => ({ ...prev, [id]: e.target.value}))} 
                        />
                        {state[id] === opt && <i className='fas fa-circle' />}
                    </label>
                    {opt}
                </div>
            ))}
        </div>
    );
};

const Checklist = ({ state, setState, field }) => {
    const { id, name, options, required } = field;
    
    const handleTextClick = (opt) => {
        setState(prev => {
            const currentOptions = prev[id] || [];
            if (currentOptions.includes(opt)) {
                return {
                    ...prev,
                    [id]: currentOptions.filter(x => x !== opt)
                }
            } else {
                return {
                    ...prev,
                    [id]: [...currentOptions, opt]
                }
            }
        });
    };
    
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            {options.map((opt, index) => (
                <div className={styles.checkboxGroup} key={opt + index}>
                    <label htmlFor={id+index} className={styles.checkbox}>
                        {state[id]?.includes(opt) && <i className='fas fa-check' />}
                        <input 
                            type='checkbox' 
                            id={id+index} 
                            checked={state[id]?.includes(opt) ?? false} 
                            onChange={e => setState(prev => {
                                const currentOptions = prev[id] || [];
                                if (e.target.checked) {
                                    return {
                                        ...prev,
                                        [id]: [...currentOptions, opt]
                                    }
                                } else {
                                    return {
                                        ...prev,
                                        [id]: currentOptions.filter(x => x !== opt)
                                    }
                                }
                            })} 
                        />
                    </label>
                    <span 
                        onClick={() => handleTextClick(opt)} 
                        onMouseDown={(e) => e.preventDefault()}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                    >{opt}</span>
                </div>
            ))}
        </div>
    );
};

const Checkbox = ({ state, setState, field }) => {
    const { id, name, required } = field;
    
    const handleLabelClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setState(prev => ({ ...prev, [id]: !prev[id] }));
    };
    
    return (
        <div className={styles.inputGroup} style={{flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: '0 .5rem'}}>
            <label htmlFor={id} className={styles.checkbox}>
                {state[id] && <i className='fas fa-check' />}
                <input type='checkbox' id={id} className={styles.checkbox} checked={state[id] || false} onChange={e => setState(prev => ({ ...prev, [id]: e.target.checked }))} />
            </label>
            <span 
                onClick={handleLabelClick} 
                onMouseDown={(e) => e.preventDefault()}
                style={{ cursor: 'pointer', userSelect: 'none' }}
            >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</span>
        </div>
    );
};

const IconRating = ({ state, setState, field }) => {
    const { id, name, required, icon } = field;

    const iconObj = useMemo(() => {
        const obj = {};
        switch (icon) {
            case 'hearts':
                obj.unselected = 'far fa-heart';
                obj.selected = 'fas fa-heart';
                obj.checked = 'red';
                break;
            case 'thumbs-up':
                obj.unselected = 'far fa-thumbs-up';
                obj.selected = 'fas fa-thumbs-up';
                obj.checked = '#161d2d';
                break;
            case 'flames':
                obj.unselected = 'far fa-fire';
                obj.selected = 'fa-duotone fa-fire';
                obj.checked = '#e14714';
                obj.checked2 = '#ff9b29';
                break;
            case 'emotions':
                obj.unselected = 'far fa-face-meh';
                obj.selected = 'far fa-face-angry';
                obj.selected2 = 'far fa-face-frown';
                obj.selected3 = 'far fa-face-grin';
                obj.selected4 = 'far fa-face-grin-wide';
                obj.selected5 = 'far fa-face-laugh-beam';
                obj.checked = 'red';
                obj.checked2 = 'orangered';
                obj.checked3 = 'purple';
                obj.checked4 = 'blue';
                obj.checked5 = 'green';
                break;
            case 'stars':
            default:
                obj.unselected = 'far fa-star';
                obj.selected = 'fas fa-star';
                obj.checked = 'gold';
                break;
        };

        return obj;
    },[icon]);

    const handleRating = (e) => {
        const selectedRating = parseInt(e.target.id.split('-')[1]);
        setState(prev => ({ ...prev, [id]: selectedRating}));
    };

    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div className={styles.icons}>
                <div id='icon-1' className={state[id] === 1 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating} onClick={handleRating}>
                    <i className={state[id] > 0 ? iconObj.selected : iconObj.unselected} style={state[id] > 0 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-2' className={state[id] === 2 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating} onClick={handleRating}>
                    <i className={state[id] > 1 ? icon === 'emotions' ? iconObj.selected2 : iconObj.selected : iconObj.unselected} style={state[id] > 1 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked2} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-3' className={state[id] === 3 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating} onClick={handleRating}>
                    <i className={state[id] > 2 ? icon === 'emotions' ? iconObj.selected3 : iconObj.selected : iconObj.unselected} style={state[id] > 2 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked3} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-4' className={state[id] === 4 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating} onClick={handleRating}>
                    <i className={state[id] > 3 ? icon === 'emotions' ? iconObj.selected4 : iconObj.selected : iconObj.unselected} style={state[id] > 3 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked4} : {color: iconObj.checked} : null} />
                </div>
                <div id='icon-5' className={state[id] === 5 ? `${styles.iconRating} ${styles.selected}` : styles.iconRating} onClick={handleRating}>
                    <i className={state[id] > 4 ? icon === 'emotions' ? iconObj.selected5 : iconObj.selected : iconObj.unselected} style={state[id] > 4 ? icon === 'flames' ? {'--fa-primary-color': iconObj.checked, '--fa-secondary-color': iconObj.checked2, '--fa-secondary-opacity': '1'} : icon === 'emotions' ? {color: iconObj.checked5} : {color: iconObj.checked} : null} />
                </div>
            </div>
        </div>
    );
};

const OpinionScale = ({ state, setState, field }) => {
    const { id, name, required, scale } = field;

    const handleRating = (e) => {
        const selectedRating = parseInt(e.target.id.split('-')[1]);
        setState(prev => ({ ...prev, [id]: selectedRating}));
    };

    const numbers = () => {
        let numbs = [];
        let spread = scale ? (scale > 10 ? 10 : scale) : 5;
        for (let i = 1; i <= spread; i++) {
            numbs.push(i);
        };

        return (
            <>
            {numbs.filter(n => n !== 10).map((n,i) => (
                <button id={`radio-${n}`} key={i} className={state[id] === n ?  `${styles.opinion} ${styles.selected}` : styles.opinion} onClick={handleRating}>
                    <i className={`far fa-${n}`} style={{pointerEvents: 'none'}} />
                </button>
            ))}
            {numbs.filter(n => n === 10).map((n,i) => (
                <button id={`radio-${n}`} key={n} className={state[id] === n ? ` ${styles.opinion} ${styles.selected}` : styles.opinion} onClick={handleRating}>
                    <i className='far fa-1' style={{pointerEvents: 'none'}} />
                    <i className='far fa-0' style={{pointerEvents: 'none'}} />
                </button>
            ))}
            </>
        );
    };

    return (
        <div className={styles.inputGroup}>
            <label className={styles.label} >{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div className={styles.opinions}>
                {numbers()}
            </div>
        </div>
    );
};

const Header = ({ field }) => {
    const { name } = field;
    const defaultStyles = {
        fontSize: '2rem',
        fontWeight: '700',
        textAlign: 'center'
    };
    const appliedStyles = {
        ...defaultStyles,
        ...(field.styles || {}),
        margin: 0
    };
    return (
        <div className={styles.inputGroup}>
            <h1 className={styles.readonlyText} style={appliedStyles}>{name || ''}</h1>
        </div>
    );
};

const Paragraph = ({ field }) => {
    const { name } = field;
    const defaultStyles = {
        fontSize: '16px',
        textAlign: 'center'
    };
    const appliedStyles = {
        ...defaultStyles,
        ...(field.styles || {})
    };
    return (
        <div className={styles.inputGroup}>
            <span className={styles.readonlyTextarea} style={appliedStyles}>{name || ''}</span>
        </div>
    );
};

const HeaderParagraph = ({ field }) => {
    const nameValue = field?.name && typeof field.name === 'object'
        ? field.name
        : { header: '', paragraph: '' };
    const styleGroups = field?.styles && typeof field.styles === 'object'
        ? field.styles
        : {};
    const sharedStyles = Object.fromEntries(
        Object.entries(styleGroups).filter(([key]) => !['header', 'paragraph'].includes(key))
    );
    const headerStyles = {
        fontSize: '2rem',
        fontWeight: '700',
        textAlign: 'center',
        ...sharedStyles,
        ...(styleGroups.header || {}),
        margin: 0
    };
    const paragraphStyles = {
        fontSize: '16px',
        textAlign: 'center',
        ...sharedStyles,
        ...(styleGroups.paragraph || {})
    };

    return (
        <div className={styles.inputGroup}>
            <div className={styles.stackedTextBlock}>
                <h1 className={styles.readonlyText} style={headerStyles}>{nameValue.header || ''}</h1>
                <span className={styles.readonlyTextarea} style={paragraphStyles}>{nameValue.paragraph || ''}</span>
            </div>
        </div>
    );
};

const Divider = ({ field }) => {
    return (
        <div className={styles.inputGroup}>
            <hr className={styles.divider} />
        </div>
    );
};

const Banner = ({ field }) => {
    const { name, options } = field;
    const showIcon = options && options.includes('show-icon');
    const bannerColor = field.styles?.color || '#607ca7';
    const bannerBgAlpha = (hex, alpha = 0.12) => {
        let c = (hex || '').replace('#', '').trim();
        if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
        if (c.length !== 6) return `rgba(96, 124, 167, ${alpha})`;
        const num = parseInt(c, 16);
        if (isNaN(num)) return `rgba(96, 124, 167, ${alpha})`;
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    const bannerBackground = bannerBgAlpha(bannerColor, 0.12);
    const defaultStyles = {
        backgroundColor: bannerBackground,
        border: `2px solid ${bannerColor}`,
        color: bannerColor,
        textAlign: 'left'
    };
    const appliedStyles = {
        ...defaultStyles,
        ...(field.styles || {})
    };
    const iconColor = field.styles?.color || '#607ca7';
    return (
        <div className={styles.inputGroup}>
            <div className={styles.banner} style={appliedStyles}>
                {showIcon && <i className='far fa-lightbulb-on' style={{ color: iconColor, fontSize: '0.75em', marginRight: '0.5rem', flexShrink: 0 }} />}
                <span>{name || ''}</span>
            </div>
        </div>
    );
};

const SlidingScale = ({ state, setState, field }) => {
    const { id, name, required } = field;
    const value = state[id] || '-';
    
    return (
        <div className={styles.inputGroup}>
            <label className={styles.label}>{required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', width: '100%' }}>
                <div className={styles.slidingScale}>
                    <input 
                        type='range' 
                        min='0' 
                        max='100'
                        value={value}
                        onChange={e => setState(prev => ({ ...prev, [id]: e.target.value === '-' ? null : parseInt(e.target.value) }))}
                    />
                </div>
                <div style={{ minWidth: '40px', textAlign: 'center', fontWeight: 'bold', color: '#607ca7' }}>{value}</div>
            </div>
        </div>
    );
};

const FileUpload = ({ state, setState, field, qrId, logId }) => {
    const { id, name, required } = field;
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const workerRef = useRef(null);

    useEffect(() => {
        workerRef.current = new Worker(new URL('../../../workers/imageOptimizer.worker.js', import.meta.url));
        
        workerRef.current.onmessage = async (event) => {
            const { processedImages, error: workerError } = event.data || {};
            
            if (workerError) {
                console.error('Worker error:', workerError);
                setError('Failed to process image');
                setUploading(false);
                return;
            }
            
            if (processedImages && processedImages.length > 0) {
                try {
                    // Create file from processed image
                    const processedImg = processedImages[0];
                    const file = new File([processedImg.buffer], `${logId}.webp`, {
                        type: 'image/webp',
                        lastModified: Date.now()
                    });
                    
                    // Upload to storage
                    const formData = new FormData();
                    formData.append('file', file, file.name);
                    const { data: uploaded } = await API.storeFiles(formData);
                    
                    if (Array.isArray(uploaded) && uploaded.length > 0) {
                        setState(prev => ({ ...prev, [id]: uploaded[0] }));
                    }
                } catch (err) {
                    console.error('Error uploading file:', err);
                    setError('Upload failed');
                } finally {
                    setUploading(false);
                }
            }
        };

        workerRef.current.onerror = (err) => {
            console.error('Worker error:', err);
            setError('Processing failed');
            setUploading(false);
        };

        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
            }
        };
    }, [id, logId, setState]);

    const handleFileSelect = async (files) => {
        if (!files || files.length === 0) return;
        
        setError('');
        const file = files[0];
        
        // Validate file
        const acceptable = isFileAcceptable(file, ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'], 5000000);
        if (!acceptable.bool) {
            setError(acceptable.reason || 'Invalid file');
            return;
        }
        
        try {
            setUploading(true);
            
            // Handle GIF separately
            if (file.type === 'image/gif') {
                const formData = new FormData();
                formData.append('file', file);
                const { data } = await API.optimizeGif(formData);
                
                // Store the optimized GIF URL
                setState(prev => ({ ...prev, [id]: { url: data, name: `${logId}.webp`, path: `temp_files/${logId}.webp` } }));
                setUploading(false);
                return;
            }
            
            // Process image with worker
            const arrayBuffer = await file.arrayBuffer();
            const fileMetadata = {
                name: file.name,
                type: file.type,
                lastModified: file.lastModified || Date.now(),
            };
            
            workerRef.current.postMessage({
                buffer: arrayBuffer,
                id: logId,
                metadata: fileMetadata,
                type: 'qr_files',
            }, [arrayBuffer]);
            
        } catch (err) {
            console.error('Error processing file:', err);
            setError('Failed to process file');
            setUploading(false);
        }
    };

    return (
        <div className={styles.inputGroup}>
            <label htmlFor={id} className={styles.label}>
                {required ? <>{name}<sup><i className='fa-regular fa-asterisk' /></sup></> : name}
            </label>
            {state?.[id]?.url ? (
                <div className={styles.filePreview}>
                    <img
                        src={state[id].url}
                        alt={(state[id]?.name || name || 'uploaded file')}
                        className={styles.previewImage}
                    />
                    <div className={styles.fileName}>
                        {state[id]?.name || state[id]?.path?.split('/').pop() || state[id]?.url?.split('/').pop() || 'Uploaded file'}
                    </div>
                    <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={() => {
                            setError('');
                            setState(prev => ({ ...prev, [id]: undefined }));
                        }}
                        disabled={uploading}
                    >
                        Remove File
                    </button>
                </div>
            ) : (
                <FileInput
                    id={id}
                    name={name}
                    onFileSelect={handleFileSelect}
                    acceptedFileTypes={'image/*'}
                    multiple={false}
                    label={uploading ? 'Processing...' : 'Choose File'}
                    disabled={uploading}
                />
            )}
            {error && <div className={styles.error}>{error}</div>}
        </div>
    );
};

const FieldTypes = ({ configTypes, field, state, setState, sigCanvas, setSigCanvas, edit, qrId, logId }) => {
    const conf = configTypes?.[field?.type] || {};
    switch (conf?.type) {
        case 'text':
            return <TextBox state={state} setState={setState} field={field} />;
        case 'email':
            return <Email state={state} setState={setState} field={field} />;
        case 'phone':
            return <Phone state={state} setState={setState} field={field} />;
        case 'address':
            return <Address state={state} setState={setState} field={field} />;
        case 'textarea':
            return <TextArea state={state} setState={setState} field={field} />;
        case 'number':
            return <Number state={state} setState={setState} field={field} />;
        case 'select':
            return <Dropdown state={state} setState={setState} field={field} />;
        case 'signature':
            return <Signature field={field} sigCanvas={sigCanvas} setSigCanvas={setSigCanvas} setState={setState} edit={edit} />;
        case 'radio':
            return <SingleSelect state={state} setState={setState} field={field} />;
        case 'icon-rating':
            return <IconRating state={state} setState={setState} field={field} />;
        case 'num-rating':
            return <OpinionScale state={state} setState={setState} field={field} />;
        case 'checkbox':
            if (conf?.multiple) {
                return <Checklist state={state} setState={setState} field={field} />
            } else {
                return <Checkbox state={state} setState={setState} field={field} />;
            };
        case 'datetime-local':
            return <Timestamp state={state} setState={setState} field={field} isTimestamp={conf.type === 'date'} />;
        case 'header':
            return <Header field={field} />;
        case 'paragraph':
            return <Paragraph field={field} />;
        case 'headerParagraph':
            return <HeaderParagraph field={field} />;
        case 'divider':
            return <Divider field={field} />;
        case 'banner':
            return <Banner field={field} />;
        case 'slide-scale':
            return <SlidingScale state={state} setState={setState} field={field} />;
        case 'file':
            return <FileUpload state={state} setState={setState} field={field} qrId={qrId} logId={logId} />;
        default:
            return <></>;
    };
};

export default FieldTypes;
